import { Link } from 'react-router-dom';
import { Progress } from '../ui/progress';
import { usePlanContext } from '../../hooks/usePlanContext';

function meterLabel(used, limit) {
  if (limit == null || limit < 0) return `${used} / ∞`;
  return `${used} / ${limit}`;
}

export default function SidebarPlanUsage({ organizationId }) {
  const { context, loading } = usePlanContext(organizationId);

  if (loading || !context || context.plan_id === 'corporate') return null;

  const spacesUsed = context.usage?.spaces_count ?? 0;
  const membersUsed = context.usage?.team_members_count ?? 0;
  const domainsUsed = context.usage?.custom_domains_count ?? 0;
  const maxSpaces = context.limits?.max_spaces;
  const maxMembers = context.limits?.effective_member_capacity ?? context.limits?.max_users;
  const maxDomains = context.limits?.effective_custom_domain_capacity;

  const pct = (used, lim) => {
    if (lim == null || lim < 0) return 0;
    return Math.min(100, (used / Math.max(1, lim)) * 100);
  };

  const nearLimit =
    (maxSpaces >= 0 && spacesUsed >= maxSpaces) ||
    (maxMembers >= 0 && membersUsed >= maxMembers) ||
    (maxDomains >= 0 && domainsUsed >= maxDomains);

  return (
    <div className="mx-3 mb-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/50 p-3 space-y-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-700 dark:text-slate-300 truncate">
          {context.plan_name || 'Plan'}
        </span>
        {nearLimit && (
          <Link to="/dashboard/pricing" className="text-blue-600 dark:text-blue-400 shrink-0 hover:underline">
            Upgrade
          </Link>
        )}
      </div>
      <div className="space-y-1.5">
        <div>
          <div className="flex justify-between text-slate-500 mb-0.5">
            <span>Spaces</span>
            <span>{meterLabel(spacesUsed, maxSpaces)}</span>
          </div>
          {maxSpaces >= 0 && <Progress value={pct(spacesUsed, maxSpaces)} className="h-1" />}
        </div>
        <div>
          <div className="flex justify-between text-slate-500 mb-0.5">
            <span>Members</span>
            <span>{meterLabel(membersUsed, maxMembers)}</span>
          </div>
          {maxMembers >= 0 && <Progress value={pct(membersUsed, maxMembers)} className="h-1" />}
        </div>
        <div>
          <div className="flex justify-between text-slate-500 mb-0.5">
            <span>Domains</span>
            <span>{meterLabel(domainsUsed, maxDomains)}</span>
          </div>
          {maxDomains >= 0 && <Progress value={pct(domainsUsed, maxDomains)} className="h-1" />}
        </div>
      </div>
    </div>
  );
}
